import { isMagneticNode } from '../constants/nodeTypes';

export interface PolarSocket {
  id: string;
  type: 'input' | 'output';
}

export interface PolarNode {
  id: string;
  sockets: PolarSocket[];
}

export interface NormalizedConnection {
  sourceNodeId: string;
  sourceSocketId: string;
  targetNodeId: string;
  targetSocketId: string;
}

export interface NormalizeConnectionOptions {
  /**
   * Drawing/magnetic shapes: keep the sockets the user actually dragged
   * between. Drag-from is the source, drop-on is the target.
   */
  freeform?: boolean;
}

/** True when a drawing shape is involved — sockets stay polarity-agnostic until connected. */
export function connectionUsesFreeformSockets(sourceType: string, targetType: string): boolean {
  return isMagneticNode(sourceType) || isMagneticNode(targetType);
}

/**
 * Stored architecture edges are always output → input.
 * Dragging the reverse way (input of A to output of B) swaps endpoints
 * so the arrow still flows producer → consumer.
 *
 * Freeform (drawing shapes): do not remap — drag order is the direction.
 */
export function normalizeOutputToInput(
  source: PolarNode,
  sourceSocketId: string,
  target: PolarNode,
  targetSocketId: string,
  options?: NormalizeConnectionOptions,
): NormalizedConnection | null {
  if (source.id === target.id) return null;
  const ss = source.sockets.find(s => s.id === sourceSocketId);
  const ts = target.sockets.find(s => s.id === targetSocketId);
  if (!ss || !ts) return null;

  if (options?.freeform) {
    return {
      sourceNodeId: source.id,
      sourceSocketId: ss.id,
      targetNodeId: target.id,
      targetSocketId: ts.id,
    };
  }

  if (ss.type === 'output' && ts.type === 'input') {
    return {
      sourceNodeId: source.id,
      sourceSocketId: ss.id,
      targetNodeId: target.id,
      targetSocketId: ts.id,
    };
  }

  if (ss.type === 'input' && ts.type === 'output') {
    return {
      sourceNodeId: target.id,
      sourceSocketId: ts.id,
      targetNodeId: source.id,
      targetSocketId: ss.id,
    };
  }

  if (ss.type === 'input' && ts.type === 'input') {
    const tgtOut = target.sockets.find(s => s.type === 'output');
    if (!tgtOut) return null;
    return {
      sourceNodeId: target.id,
      sourceSocketId: tgtOut.id,
      targetNodeId: source.id,
      targetSocketId: ss.id,
    };
  }

  const tgtIn = target.sockets.find(s => s.type === 'input');
  if (!tgtIn) return null;
  return {
    sourceNodeId: source.id,
    sourceSocketId: ss.id,
    targetNodeId: target.id,
    targetSocketId: tgtIn.id,
  };
}

/**
 * Dragging out of a drawing shape is always an output; dropping on one is always
 * an input — independent of the idle left/top vs right/bottom defaults.
 */
export function applyMagneticDragPolarity(
  source: { type: string; sockets: PolarSocket[] },
  sourceSocketId: string,
  target: { type: string; sockets: PolarSocket[] },
  targetSocketId: string,
): void {
  if (isMagneticNode(source.type)) {
    const sock = source.sockets.find(s => s.id === sourceSocketId);
    if (sock) sock.type = 'output';
  }
  if (isMagneticNode(target.type)) {
    const sock = target.sockets.find(s => s.id === targetSocketId);
    if (sock) sock.type = 'input';
  }
}

/** Edge-role of a socket: source = purple (output), target = green (input).
 *  A socket that is a source on any edge stays a source (purple), even if another
 *  edge also lands on it.
 */
export function socketEdgeRoles(
  edges: Array<{ sourceNodeId: string; sourceSocketId: string; targetNodeId: string; targetSocketId: string }>,
): Map<string, 'source' | 'target'> {
  const roles = new Map<string, 'source' | 'target'>();
  for (const edge of edges) {
    roles.set(`${edge.sourceNodeId}-${edge.sourceSocketId}`, 'source');
  }
  for (const edge of edges) {
    const key = `${edge.targetNodeId}-${edge.targetSocketId}`;
    if (!roles.has(key)) roles.set(key, 'target');
  }
  return roles;
}
